/*
 * SundayBuilder · Screen 2 · Context Notes
 * ---------------------------------------------------------------------
 * One optional textarea plus a Choral Intelligence™ rail (desktop) /
 * drawer (mobile) that surfaces governed anthem candidates for this
 * Sunday. The rail uses the same deterministic pipeline as the inline
 * slot picker on Screen 4 — no second algorithm.
 * =================================================================== */
import { useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import SundayBuilderChoralRail from "../../features/choral-intelligence/SundayBuilderChoralRail";
import "../../features/choral-intelligence/choral-intelligence.css";

export default function ContextNotes({ sunday, onBack, onPrepare, initialNotes = "" }) {
  const [notes, setNotes] = useState(initialNotes);

  const meta = sunday
    ? `${sunday.label}${sunday.occasion ? ` · ${sunday.occasion}` : ""}`
    : "";
  const date = sunday?.iso || null;

  return (
    <div className="sb-screen" data-testid="sb-context-screen">
      <div className="sb-context-shell">
        <div className="sb-context-main">
          {meta && <p className="sb-today">{meta}</p>}
          <h1 className="sb-h1">What is unique about this celebration?</h1>
          <p
            className="sb-context-support"
            data-testid="sb-context-supporting"
            style={{
              fontFamily: "var(--li-font-body)",
              fontSize: "1.0625rem",
              color: "var(--li-text-secondary)",
              margin: "0 0 24px",
              maxWidth: "62ch",
              lineHeight: 1.55,
            }}
          >
            Tell us only what the platform cannot already know — for example a
            baptism, memorial, guest musicians, diocesan celebration, pastoral
            emphasis, or other circumstances unique to this liturgy.
          </p>

          <div style={{ marginBottom: 32 }}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Describe anything unique about this celebration…"
              data-testid="sb-context-notes"
              style={{
                width: "100%",
                minHeight: 120,
                padding: "14px 16px",
                fontSize: "1.0625rem",
                fontFamily: "var(--li-font-body)",
                backgroundColor: "var(--li-parchment-fine)",
                color: "var(--li-text-primary)",
                border: "1px solid var(--li-border)",
                borderRadius: "var(--li-radius-card)",
                resize: "vertical",
                lineHeight: 1.55,
              }}
            />
            <p style={{
              fontFamily: "var(--li-font-body)",
              fontSize: "0.9375rem",
              color: "var(--li-text-muted)",
              margin: "10px 0 0",
            }}>
              Optional. Leave blank if this is a standard Sunday.
            </p>
          </div>

          {/* Mobile drawer trigger — desktop rail is in the right column. */}
          {date && (
            <div className="sb-context-mobile-rail">
              <SundayBuilderChoralRail date={date} variant="drawer" limit={5} />
            </div>
          )}

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 48,
          }}>
            <button
              className="sb-btn ghost"
              onClick={onBack}
              data-testid="sb-context-back"
            >
              <ChevronLeft size={18} strokeWidth={2.5} /> Back
            </button>
            <button
              className="sb-btn primary"
              onClick={() => onPrepare(notes)}
              data-testid="sb-context-prepare"
            >
              Prepare dossier
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {date && (
          <div className="sb-context-aside">
            <SundayBuilderChoralRail date={date} variant="rail" limit={5} />
          </div>
        )}
      </div>

      <style>{`
        .sb-context-shell {
          display: grid;
          grid-template-columns: 1fr;
          gap: 40px;
          align-items: start;
        }
        @media (min-width: 901px) {
          .sb-context-shell {
            grid-template-columns: minmax(0, 1fr) 320px;
          }
          .sb-context-mobile-rail { display: none; }
        }
        @media (max-width: 900px) {
          .sb-context-aside { display: none; }
          .sb-context-mobile-rail { margin: 20px 0 4px; }
        }
      `}</style>
    </div>
  );
}
